import { useCountdownStore } from '@/stores/countdown-store';
import { cn } from '@/lib/utils';

/** Seconds in an hour / minute, for HH:MM:SS formatting */
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/** Zero-pad a number to two digits */
function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** Format an absolute second count as HH:MM:SS (hours uncapped). */
function formatClock(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Oversized T-minus / T-plus readout. `targetMs` is the T-0 epoch; null renders
 * a placeholder. Distinct styling for count-down (yellow) vs count-up (green).
 */
export function CountdownDisplay({ targetMs }: { targetMs: number | null }): React.JSX.Element {
  const nowMs = useCountdownStore((s) => s.nowMs);

  if (targetMs === null) {
    return (
      <div className="flex flex-col items-center py-8">
        <span className="font-mono text-4xl font-bold tracking-widest text-muted-foreground">--:--:--</span>
        <span className="mt-2 text-xs text-muted-foreground">No T-0 selected</span>
      </div>
    );
  }

  const deltaSeconds = Math.round((targetMs - nowMs) / 1000);
  const isCountUp = deltaSeconds < 0;
  const sign = isCountUp ? 'T+' : 'T-';

  return (
    <div className="flex flex-col items-center py-8">
      <div className="flex items-baseline gap-2">
        <span className={cn('font-mono text-2xl font-bold', isCountUp ? 'text-green-400' : 'text-echo-yellow')}>
          {sign}
        </span>
        <span
          className={cn(
            'font-mono text-5xl font-bold tracking-widest tabular-nums',
            isCountUp ? 'text-green-400' : 'text-echo-yellow',
          )}
        >
          {formatClock(Math.abs(deltaSeconds))}
        </span>
      </div>
      <span className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">
        {isCountUp ? 'Elapsed since T-0' : 'Time to T-0'}
      </span>
    </div>
  );
}
